import { useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2, Gift, Zap } from 'lucide-react';
import { GiftCardTile } from './GiftCardTile';
import { useGiftCardCart } from '../context/GiftCardCartContext';
import { GIFT_CARD_MAX_QUANTITY } from '../lib/rewardCatalogue';
import { formatINR } from '../lib/money';

/**
 * The gift-card half of the cart.
 *
 * Its own block rather than mixed into the product list, because a voucher
 * shares almost nothing with a physical line: no shipping, no GST row, no
 * address, and it is issued the moment payment clears. Folding it into the
 * product totals would put a delivery charge and a tax figure against something
 * that is neither delivered nor taxed here.
 *
 * ## Every figure comes from `POST /giftcards/quote`
 *
 * Nothing on this screen is worked out here — not even quantity × price. The
 * quote runs the same code path as `/purchase/bulk`, so what is shown is what
 * will be charged by construction, rather than because two implementations
 * happen to agree today. Per line it answers `face_value`, `discount_value`,
 * `discount_amount` and `payable_amount`, plus the line totals already
 * multiplied out: `line_face_value`, `line_discount`, `line_total`.
 *
 * The stored amount survives only as the fallback for the moment before the
 * first quote lands and for a quote that fails — a cart the customer can see
 * must not blank because one request did.
 *
 * Checkout lives in GiftCardCartCheckout, next to the totals: a cart means "buy
 * all of this", so there is one button for the basket rather than one per row.
 *
 * There is deliberately no re-pricing control. A wrong amount is fixed by
 * removing the line and adding the right one, which is two taps and always
 * works; an in-row picker only had something to show for cards with a fixed
 * denomination list, and rendered an empty box for every range card.
 */
export function GiftCardCartSection() {
  const navigate = useNavigate();
  const { items, count, total, quote, lineQuote, setQuantity, removeGiftCard } = useGiftCardCart();

  if (!items.length) return null;

  // Plain URL — the product page looks the reward up by id, so nothing has to be
  // carried across in router state beyond what was already chosen here.
  const openCard = (line) => navigate(`/product/reward_${line.reward_id}`, {
    state: { denomination: line.denomination, quantity: line.quantity },
  });

  return (
    <section className="space-y-4" data-testid="gift-card-cart">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-heading text-xl text-secondary">
          <Gift className="h-5 w-5 text-primary" />
          Gift Cards
        </h2>
        <span className="font-body text-[13px] text-muted-foreground">
          {quote?.card_count ?? count} {(quote?.card_count ?? count) === 1 ? 'card' : 'cards'}
        </span>
      </div>

      <div className="space-y-3">
        {items.map((line) => {
          const priced = lineQuote(line);
          const blocked = priced && priced.available === false;
          // The provider's per-line cap, as the server states it for this card.
          const maxQty = priced?.max_quantity ?? GIFT_CARD_MAX_QUANTITY;
          const isPercent = priced?.markup_direction === 'surcharge'
            ? priced?.surcharge_type === 'percentage'
            : priced?.discount_type === 'percentage';

          return (
            <div
              key={line.line_id}
              className={`rounded-3xl border bg-white p-4 ${blocked
                ? 'border-destructive/40 bg-destructive/[0.02]'
                : 'border-slate-200/70'}`}
              data-testid={`gift-card-line-${line.line_id}`}
            >
              <div className="flex gap-4">
                <button onClick={() => openCard(line)} className="w-24 flex-shrink-0 self-start md:w-32">
                  <GiftCardTile
                    reward={{ name: line.name, display_image_candidates: line.image_candidates }}
                    size="sm"
                  />
                </button>

                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="line-clamp-1 font-body font-semibold text-secondary">
                      {priced?.name || line.name}
                    </h3>
                    <button
                      onClick={() => removeGiftCard(line.line_id)}
                      className="-mr-1 -mt-1 flex-shrink-0 rounded-full p-2 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                      aria-label={`Remove ${line.name}`}
                      data-testid={`remove-gift-card-${line.line_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  {blocked ? (
                    <p className="mt-1 font-body text-[12px] font-semibold text-destructive">
                      {priced.reason || 'Not available right now — remove to continue'}
                    </p>
                  ) : (
                    <p className="mt-0.5 inline-flex items-center gap-1 font-body text-[11px] text-success">
                      <Zap className="h-3 w-3" /> Instant delivery — no shipping
                    </p>
                  )}

                  {/* The line, spelled out. Every figure is the quote's, and the
                      line totals arrive already multiplied by quantity. */}
                  <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 font-body text-[13px] sm:grid-cols-4">
                    <div>
                      <dt className="text-[11px] text-muted-foreground">Gift card value</dt>
                      <dd className="font-semibold text-secondary">
                        {formatINR(priced?.line_face_value ?? line.denomination * line.quantity)}
                      </dd>
                    </div>

                    {priced?.markup_direction === 'surcharge' ? (
                      <>
                        {priced?.surcharge_value > 0 && (
                          <div>
                            <dt className="text-[11px] text-muted-foreground">Surcharge</dt>
                            <dd className="font-semibold text-amber-600">
                              {isPercent
                                ? `+${Number(Number(priced.surcharge_value).toFixed(2))}%`
                                : `+${formatINR(priced.surcharge_value)}`}
                            </dd>
                          </div>
                        )}
                        {priced?.line_surcharge > 0 && (
                          <div>
                            <dt className="text-[11px] text-muted-foreground">Total surcharge</dt>
                            <dd className="font-semibold text-amber-600">
                              +{formatINR(priced.line_surcharge)}
                            </dd>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        {priced?.discount_value > 0 && (
                          <div>
                            <dt className="text-[11px] text-muted-foreground">Discount</dt>
                            <dd className="font-semibold text-success">
                              {isPercent
                                ? `${Number(Number(priced.discount_value).toFixed(2))}%`
                                : formatINR(priced.discount_value)}
                            </dd>
                          </div>
                        )}
                        {priced?.line_discount > 0 && (
                          <div>
                            <dt className="text-[11px] text-muted-foreground">You saved</dt>
                            <dd className="font-semibold text-success">
                              {formatINR(priced.line_discount)}
                            </dd>
                          </div>
                        )}
                      </>
                    )}

                    <div>
                      <dt className="text-[11px] text-muted-foreground">Payable</dt>
                      <dd className="font-bold text-secondary">
                        {formatINR(priced?.line_total ?? line.denomination * line.quantity)}
                      </dd>
                    </div>
                  </dl>

                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <div className="flex items-center rounded-full border border-gray-200 bg-white">
                      <button
                        onClick={() => setQuantity(line.line_id, line.quantity - 1)}
                        className="rounded-l-full p-2 transition-colors hover:bg-surface"
                        aria-label="Decrease quantity"
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <span className="w-10 text-center font-body text-sm">{line.quantity}</span>
                      <button
                        onClick={() => setQuantity(line.line_id, line.quantity + 1)}
                        disabled={line.quantity >= maxQty}
                        title={line.quantity >= maxQty
                          ? `${maxQty} is the most of one card at one value that can be issued in a single order`
                          : undefined}
                        className="rounded-r-full p-2 transition-colors hover:bg-surface disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                        aria-label="Increase quantity"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>

                    {priced?.payable_amount != null && (
                      <span className="font-body text-[12px] text-muted-foreground">
                        {formatINR(priced.payable_amount)} each
                      </span>
                    )}
                  </div>

                  {priced?.quantity_exceeded && (
                    <p className="mt-2 font-body text-[11px] font-semibold text-destructive">
                      Only {maxQty} of this card can be issued in one order.
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* The stored figure, shown only while the first quote is in flight. It is
          never what checkout charges. */}
      {!quote && (
        <p className="font-body text-[11px] text-muted-foreground">
          Pricing {formatINR(total)} — confirming with the server…
        </p>
      )}
    </section>
  );
}

export default GiftCardCartSection;
